import { dateFns as moment } from '@pcln/date-fns-utils'
import PCLNAnalytics from '@pcln/analytics'
import first from 'lodash/first'
import keys from 'lodash/keys'
import random from 'lodash/random'

import { createGaLabelsForRanges } from '../create-ga-labels-for-ranges'
import { adModel } from './AdHolderModel.js'
import { ranges } from './ranges.js'
import { isServerSide } from '../isServerSide'

function fireGtm(type, eventName, category, action, label, value, callback) {
  if (!isServerSide) {
    // Log event to Splunk (this used to be handled by np-pcln-gtm)
    const tag = {
      action,
      category,
    }

    if (label) {
      tag.label = label
    }

    if (value) {
      // PCLNAnalytics ignores everything that isn't a string, so we need to stringify value
      tag.value = JSON.stringify(value)
    }

    PCLNAnalytics.log(tag)

    const event = {
      event: eventName,
      'gtm-event-action': action,
      'gtm-event-category': category,
      'gtm-event-label': label,
      'gtm-event-value': value,
      eventCallback: callback,
      eventTimeout: 1000,
    }

    // As this function is called repeatedly, this has the effect of staggering the execution
    // of Google Tag Manager updates (updating dataLayer, not to be confused with using PCLNAnalytics.logEvent)
    const delay = random(0, 500)
    requestIdleCallback(() => setTimeout(() => window?.dataLayer?.push(event), delay), {
      timeout: 15000, // 15 seconds
    })
  }
}

function splitGetLabel(isFused, isInterline) {
  let result

  if (isFused) {
    result = 'Split'
  } else {
    result = 'Single'
  }
  if (isInterline) {
    result += ' interline'
  }

  return result
}

const logicFunctions = {
  fireGtm,

  detailViews: 0,
  timers: {},
  retailListingPosition: 0,

  setRetailListingPosition(retailListingPosition) {
    this.retailListingPosition = retailListingPosition
  },

  incrementDetailViews() {
    this.detailViews++
  },

  startTimer(key) {
    if (isServerSide) {
      return
    }
    const timeStart =
      window.performance && window.performance.now ? window.performance.now() : new Date().getTime()
    this.timers[key] = { timeStart }
  },

  endTimer(key) {
    if (isServerSide) {
      return
    }
    const timeEnd =
      window.performance && window.performance.now ? window.performance.now() : new Date().getTime()
    this.timers[key].timeEnd = timeEnd
  },

  retrieveTimer(key) {
    const timer = this.timers[key]
    let retrievedTimes = false
    if (timer && timer.timeStart && timer.timeEnd) {
      const millisTime = timer.timeEnd - timer.timeStart
      const secondsTime = millisTime / 1000
      delete this.timers[key]
      retrievedTimes = { millisTime, secondsTime }
    }
    return retrievedTimes
  },

  fire: {
    sliceSelection(action, label) {
      fireGtm('send', 'event', 'MFLY-Listings', action, label)
    },
    filterTooltipActions(action, label) {
      fireGtm('send', 'event', 'MFLY-Listings', action, label)
    },
    itineraryPlacement(position) {
      let label = createGaLabelsForRanges(ranges.itineraryPlacementRange)(position)

      if (label === 'unknown') {
        // check to see if it's an itin from the Long Layover Box
        label = ~position.toString().indexOf('LL_') ? `Long-Layover ${position.split('_')[1]}` : label
      }

      fireGtm('send', 'event', 'MFLY-Listings', 'Listing position', label)
    },

    detailViews(numViews, callback) {
      const label = createGaLabelsForRanges(ranges.pageViewRange)(numViews)

      fireGtm('send', 'event', 'MFLY-Listings', 'Detail Pages Viewed', label, null, callback)
    },

    noSearchResults() {
      fireGtm('send', 'event', 'MFLY-Listings', 'No flights found', 'Page displayed')
    },

    logFilter({ action, label }) {
      fireGtm('send', 'event', 'MFLY-Listings', action, label)
    },

    logUpsellTray({ category, action, label }) {
      fireGtm('send', 'event', category, action, label)
    },

    clickedUpdateSearchMobile() {
      fireGtm('send', 'event', 'MFLY-Listings', 'No flights found', 'Change search clicked')
    },

    appLoad(slices) {
      let label

      const slicesCount = slices.length

      if (slicesCount < 1) {
        return
      } else if (slicesCount > 2) {
        label = 'MD'
      } else {
        label = slicesCount === 2 ? 'RT' : 'OW'
      }

      fireGtm('send', 'event', 'MFLY-Listings', 'App load', label)
    },

    airSearchCallInitiated() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Search Call Initiated')
    },

    airSearchCallDisplayed() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Search Call Displayed')
    },

    filterTiming(secondsElapsed) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Filter result time', `${secondsElapsed}`)
    },

    filterSectionAction(component, type) {
      fireGtm('send', 'event', 'MFLY-Listings', `${component} Section Filter`, type)
    },

    airlineFilterClick(airlineName) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Airline Filter', airlineName)
    },

    viewMoreFlights(pageNumber) {
      fireGtm('send', 'event', 'MFLY-Listings', 'View More Flights', pageNumber)
    },

    filterResetAllClick() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Reset All Clicked')
    },

    responseTime(timeseconds, category) {
      const time = Math.round(timeseconds)
      let label
      switch (true) {
        case time === 0:
          label = '<1'
          break
        case time === 1:
          label = '1'
          break
        case time === 2:
          label = '2'
          break
        case time === 3:
          label = '3'
          break
        case time === 4:
          label = '4'
          break
        case time >= 5 && time <= 6:
          label = '5-6'
          break
        case time >= 7 && time <= 8:
          label = '7-8'
          break
        case time >= 9 && time <= 10:
          label = '9-10'
          break
        case time >= 11 && time <= 15:
          label = '11-15'
          break
        case time >= 16 && time <= 20:
          label = '16-20'
          break
        case time >= 21:
          label = '21+'
          break
        default:
          label = 'unknown'
      }
      if (category === 'Rental Car Add on') {
        fireGtm('send', 'event', 'MFLY-Listings', category, label)
      } else {
        fireGtm('send', 'event', 'MFLY-Listings', 'Search result time', label)
      }
    },

    airSearchResponse(label, tripType) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Search result', label, tripType)
    },

    filteredResponse(label, tripType) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Filtered Search result', label, tripType)
    },

    noFilteredResults(currentFilter, allFilters) {
      const filterTypeLabel = this.createfilterTypeLabel(currentFilter, allFilters)
      fireGtm('send', 'event', 'MFLY-Listings', 'No filtered results', filterTypeLabel)
    },

    initialListingsRendering(label) {
      fireGtm('send', 'event', 'MFLY-Listings', 'initial listings rendered', label)
    },

    // AIR-22222 / AIR-22631
    numOfItinerariesReturned(num) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Itineraries Returned', 'Number of Itineraries Returned', num)
    },

    retailListingsPositionClicked(position) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Itinerary Selected - RTL', `Listing Position ${position}`)
    },

    // AIR-22631
    EXDListingsPositionClicked(position) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Itinerary Selected - SOPQ', `Listing Position ${position}`)
    },

    createfilterTypeLabel(currentFilter, allFilters) {
      let labelName = 'unknown'
      const hasMultipleFilters = !!allFilters.length
      let hasSliceFilter = false
      let hasMultiTimes = false
      if (hasMultipleFilters) {
        hasSliceFilter = allFilters.some((filterItem) => {
          return first(keys(filterItem)) === 'sliceFilter'
        })
        hasMultiTimes =
          allFilters.filter((filterItem) => {
            const filterName = first(keys(filterItem))
            return (
              filterName === 'arriveTimeStart' ||
              filterName === 'arriveTimeEnd' ||
              filterName === 'departTimeStart' ||
              filterName === 'departTimeEnd' ||
              filterName === 'sliceDurationMin' ||
              filterName === 'sliceDurationMax'
            )
          }).length > 1
        labelName = 'Multi'
        if (hasSliceFilter && hasMultiTimes) {
          labelName = `${labelName} Slice & Time`
        } else if (hasSliceFilter) {
          labelName = `${labelName} Slice`
        } else if (hasMultiTimes) {
          labelName = `${labelName} Time`
        }
      } else {
        labelName = this.trimLabelName(currentFilter)
      }

      let filterType = 'Unvailable Filter Type'

      switch (labelName) {
        case 'carriers':
        case 'includedCarriers':
          filterType = 'Airline'
          break
        case 'excludedCarriers':
          filterType = 'Airline'
          break
        case 'numOfStops':
          filterType = 'Stops'
          break
        case 'layoverMin':
        case 'layoverMax':
          filterType = 'Layover'
          break
        case 'dates':
        case 'excludedDates':
          filterType = 'Flex Date'
          break
        case 'arriveTimeStart':
        case 'arriveTimeEnd':
          filterType = 'Time Arrive'
          break
        case 'departTimeStart':
        case 'departTimeEnd':
          filterType = 'Time Depart'
          break
        case 'sliceDurationMin':
        case 'sliceDurationMax':
          filterType = 'Time Duration'
          break
        case 'destAirports':
        case 'origAirports':
          filterType = 'Airport'
          break
        case 'sliceFilter':
          filterType = 'Slice'
          break
        case 'Multi Slice':
          filterType = 'Multi with slice'
          break
        case 'Multi':
        case 'Multi Time':
          filterType = 'Multi no slice'
          break
        case 'Slice':
          filterType = labelName
          break
        default:
          filterType = 'Unknown Filter Type'
          break
      }
      return filterType
    },

    trimLabelName(rawLabel) {
      let labelName = ''
      for (const name in rawLabel) {
        const lastLetter = name.substr(name.length - 1)
        const lastLetterIsANumber = !isNaN(lastLetter)
        labelName = name
        if (lastLetterIsANumber) {
          labelName = name.slice(0, -1)
        }
      }
      return labelName
    },

    nyopAction(slot, tripType) {
      const label = this.getLocationLabel(slot)

      fireGtm('send', 'event', 'MFLY-Listings', ' NYOP banner', label, tripType)
    },

    backToTopAction() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Back to Top Button Clicked')
    },

    adsViewed() {
      const storedAdNodes = adModel.get()

      if (storedAdNodes.elems === undefined || storedAdNodes.elems.length === 0) {
        if (!storedAdNodes.cutoffLine) {
          storedAdNodes.elems = Array.prototype.slice.call(document.getElementsByClassName('ad') || [])
        } else {
          const newAds = Array.prototype.slice.call(document.getElementsByClassName('ad') || [])

          storedAdNodes.elems = newAds.filter((elem) => {
            return elem.offsetTop > storedAdNodes.cutoffLine
          })
        }

        adModel.set(storedAdNodes)
      }

      if (storedAdNodes.elems.length !== 0 && storedAdNodes.elems[0].getBoundingClientRect().bottom < 0) {
        // THis needs to be evaluated after AIR_SEARCH_REQUEST_SCHEMA declaration

        const tripType = ''

        const label = this.getLocationLabel(storedAdNodes.elems[0].getAttribute('data-ga-index'))

        fireGtm('send', 'event', 'MFLY-Listings', 'Ad views', label, tripType)

        storedAdNodes.cutoffLine = storedAdNodes.elems[0].offsetTop

        storedAdNodes.elems.shift()

        adModel.set(storedAdNodes)
      }
    },

    filterTags(changed, filterModelObject) {
      const reNumeric = /[0-9]+/i
      const reAlpha = /[A-Z][a-z]+/i
      const filterGAScenarios = {
        excludedCarriers(changed, filterModelObject, sliceId) {
          let excludedCarriers = 'excludedCarriers'
          let includedCarriers = 'includedCarriers'

          if (sliceId) {
            excludedCarriers = excludedCarriers + sliceId
            includedCarriers = includedCarriers + sliceId
          }

          if (changed[excludedCarriers].length > 0 && changed[includedCarriers]) {
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              'Airline filter',
              logicFunctions.fire.getLocationLabel(changed[includedCarriers].length - 1)
            )
          }
          return null
        },
        origAirports(changed, filterModelObject, sliceId) {
          if (sliceId > 1) {
            return null
          }
          const sliceAirports = filterModelObject.AIRPORTS[sliceId - 1]
          if (
            sliceAirports &&
            sliceAirports.originAirports.length !== changed['origAirports' + sliceId].length &&
            changed['origAirports' + sliceId].length > 0
          ) {
            const airportCity = sliceAirports.originAirports.length > 1 ? 2 : 1
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              'Departure airport filter',
              changed['origAirports' + sliceId].length,
              airportCity
            )
          }
          return null
        },
        destAirports(changed, filterModelObject, sliceId) {
          if (sliceId > 1) {
            return null
          }
          const sliceAirports = filterModelObject.AIRPORTS[sliceId - 1]
          if (
            sliceAirports &&
            sliceAirports.destinationAirports.length !== changed['destAirports' + sliceId].length &&
            changed['destAirports' + sliceId].length > 0
          ) {
            const airportCity = sliceAirports.destinationAirports.length > 1 ? 2 : 1
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              'Arrival airport filter',
              changed['destAirports' + sliceId].length,
              airportCity
            )
          }
          return null
        },
        numOfStops(changed, filterModelObject) {
          const { numOfStops } = changed
          // none or all selected
          if (!numOfStops || numOfStops === '7') {
            return null
          }
          numOfStops.split(',').forEach((stop) => {
            if (stop === '7') {
              return
            }
            const stopNumber = parseInt(stop)
            const label = filterModelObject.NUM_OF_STOPS[stopNumber].primaryLabel
            fireGtm('send', 'event', 'MFLY-Listings', 'Stop filter', label)
          })
          return null
        },
        sliceDurationMax(changed, filterModelObject, sliceId) {
          const originalFitlerValue = filterModelObject?.TIME.find((slice) => slice.sliceRefId === sliceId)
          if (
            changed['sliceDurationMax' + sliceId] < originalFitlerValue.sliceDurationMax &&
            changed['sliceDurationMax' + sliceId] !== ''
          ) {
            let labelValue =
              (changed['sliceDurationMax' + sliceId] - originalFitlerValue.sliceDurationMin) / 60 - 1
            labelValue = labelValue <= 0 ? '0' : logicFunctions.fire.getLocationLabel(labelValue)
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Duration filter hours`,
              labelValue,
              sliceId
            )
            let flexPercent =
              ((changed['sliceDurationMax' + sliceId] - originalFitlerValue.sliceDurationMin) /
                originalFitlerValue.sliceDurationMin) *
              100
            flexPercent = flexPercent <= 0 ? '0' : logicFunctions.fire.getPercentLabel(flexPercent)
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Duration filter percentage`,
              flexPercent,
              sliceId
            )
          }
          return null
        },
        layoverMin(changed) {
          const duration = (changed.layoverMin / 60).toFixed(1)
          fireGtm('send', 'event', 'MFLY-Listings', 'Layover Duration Min Filter', duration)
          return null
        },
        layoverMax(changed) {
          const duration = (changed.layoverMax / 60).toFixed(1)
          fireGtm('send', 'event', 'MFLY-Listings', 'Layover Duration Max Filter', duration)
          return null
        },
        departTimeEnd(changed, filterModelObject, sliceId) {
          // represents current change
          let departTimeStart = changed['departTimeStart' + sliceId]
          let originalFitlerValue = filterModelObject?.TIME.find((slice) => slice.sliceRefId === sliceId)
          // first fallback from the last time it was changed
          if (!departTimeStart) {
            departTimeStart = originalFitlerValue?.minTakeoffTime
          }
          // last fallback from unfiltered value
          if (!departTimeStart) {
            originalFitlerValue = filterModelObject.TIME[sliceId - 1]
            departTimeStart = originalFitlerValue.minTakeoffTime
          }
          const differenceInSeconds = moment(changed['departTimeEnd' + sliceId]).diff(moment(departTimeStart))
          const differenceInHours = parseInt(
            moment.duration(differenceInSeconds, 'milliseconds').asHours(),
            10
          )
          if (
            originalFitlerValue.minTakeoffTime !== departTimeStart ||
            originalFitlerValue.maxTakeoffTime !== changed['departTimeEnd' + sliceId]
          ) {
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Takeoff Hours (Upper Bound)`,
              logicFunctions.fire.getLocationLabel(differenceInHours - 1),
              sliceId
            )
            return { name: 'departTimeStart' + sliceId, status: true }
          }
          return null
        },
        departTimeStart(changed, filterModelObject, sliceId) {
          // represents current change
          let departTimeEnd = changed['departTimeEnd' + sliceId]
          let originalFitlerValue = filterModelObject?.TIME.find((slice) => slice.sliceRefId === sliceId)
          // first fallback from the last time it was changed
          if (!departTimeEnd) {
            departTimeEnd = originalFitlerValue?.maxTakeoffTime
          }
          // last fallback from unfiltered value
          if (!departTimeEnd) {
            originalFitlerValue = filterModelObject.TIME[sliceId - 1]
            departTimeEnd = originalFitlerValue.maxTakeoffTime
          }
          const differenceInSeconds = moment(departTimeEnd).diff(moment(changed['departTimeStart' + sliceId]))
          const differenceInHours = parseInt(
            moment.duration(differenceInSeconds, 'milliseconds').asHours(),
            10
          )
          if (
            originalFitlerValue.minTakeoffTime !== changed['departTimeStart' + sliceId] ||
            originalFitlerValue.maxTakeoffTime !== departTimeEnd
          ) {
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Takeoff Hours (Lower Bound)`,
              logicFunctions.fire.getLocationLabel(differenceInHours - 1),
              sliceId
            )
            return { name: 'departTimeEnd' + sliceId, status: true }
          }
          return null
        },
        arrivalTimeEnd(changed, filterModelObject, sliceId) {
          // represents current change
          let arrivalTimeStart = changed['arrivalTimeStart' + sliceId]
          let originalFitlerValue = filterModelObject?.TIME.find((slice) => slice.sliceRefId === sliceId)
          // first fallback from the last time it was changed
          if (!arrivalTimeStart) {
            arrivalTimeStart = originalFitlerValue?.minLandingTime
          }
          // last fallback from unfiltered value
          if (!arrivalTimeStart) {
            originalFitlerValue = filterModelObject.TIME[sliceId - 1]
            arrivalTimeStart = originalFitlerValue.minLandingTime
          }
          const differenceInSeconds = moment(changed['arrivalTimeEnd' + sliceId]).diff(
            moment(arrivalTimeStart)
          )
          const differenceInHours = parseInt(
            moment.duration(differenceInSeconds, 'milliseconds').asHours(),
            10
          )
          if (
            originalFitlerValue.minLandingTime !== arrivalTimeStart ||
            originalFitlerValue.maxLandingTime !== changed['arrivalTimeEnd' + sliceId]
          ) {
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Landing Hours (Upper Bound)`,
              logicFunctions.fire.getLocationLabel(differenceInHours - 1),
              sliceId
            )
            return { name: 'arrivalTimeStart' + sliceId, status: true }
          }
          return null
        },
        arrivalTimeStart(changed, filterModelObject, sliceId) {
          // represents current change
          let arrivalTimeEnd = changed['arrivalTimeEnd' + sliceId]
          let originalFitlerValue = filterModelObject?.TIME.find((slice) => slice.sliceRefId === sliceId)
          // first fallback from the last time it was changed
          if (!arrivalTimeEnd) {
            arrivalTimeEnd = originalFitlerValue?.maxLandingTime
          }
          // last fallback from unfiltered value
          if (!arrivalTimeEnd) {
            originalFitlerValue = filterModelObject.TIME[sliceId - 1]
            arrivalTimeEnd = originalFitlerValue.maxLandingTime
          }
          const differenceInSeconds = moment(arrivalTimeEnd).diff(
            moment(changed['arrivalTimeStart' + sliceId])
          )
          const differenceInHours = parseInt(
            moment.duration(differenceInSeconds, 'milliseconds').asHours(),
            10
          )
          if (
            originalFitlerValue.minLandingTime !== changed['arrivalTimeStart' + sliceId] ||
            originalFitlerValue.maxLandingTime !== arrivalTimeEnd
          ) {
            fireGtm(
              'send',
              'event',
              'MFLY-Listings',
              `Slice ${sliceId} - Landing Hours (Lower Bound)`,
              logicFunctions.fire.getLocationLabel(differenceInHours - 1),
              sliceId
            )
            return { name: 'arrivalTimeEnd' + sliceId, status: true }
          }
          return null
        },
      }
      // alias the names
      filterGAScenarios.arriveTimeStart = filterGAScenarios.arrivalTimeStart
      filterGAScenarios.arriveTimeEnd = filterGAScenarios.arrivalTimeEnd
      const dontRunTheseFunctions = {}
      for (const prop in changed) {
        const property = prop.match(reAlpha)[0]
        const sliceId = prop.match(reNumeric) ? parseInt(prop.match(reNumeric)[0], 10) : null
        const tagFunction = filterGAScenarios[property]
        const method = dontRunTheseFunctions[prop]
        if (tagFunction && !method && changed[prop] !== '') {
          const functionToAvoid = tagFunction(changed, filterModelObject, sliceId)
          if (functionToAvoid) {
            dontRunTheseFunctions[functionToAvoid.name] = functionToAvoid.status
          }
        }
      }
    },

    getLocationLabel(location) {
      let slot = parseInt(location)

      slot += 1

      switch (true) {
        case slot === 1:
          return '1'
        case slot === 2:
          return '2'
        case slot === 3:
          return '3'
        case slot === 4:
          return '4'
        case slot >= 5 && slot <= 6:
          return '5-6'
        case slot >= 7 && slot <= 8:
          return '7-8'
        case slot >= 9 && slot <= 10:
          return '9-10'
        case slot >= 11 && slot <= 15:
          return '11-15'
        case slot >= 16 && slot <= 20:
          return '16-20'
        case slot >= 21:
          return '21+'
        default:
          return 'unknown'
      }
    },

    getPercentLabel(percent) {
      const slot = parseInt(percent)

      switch (true) {
        case slot >= 1 && slot <= 10:
          return '1-10%'
        case slot >= 11 && slot <= 20:
          return '11-20%'
        case slot >= 21 && slot <= 30:
          return '21-30%'
        case slot >= 31 && slot <= 40:
          return '31-40%'
        case slot >= 41 && slot <= 60:
          return '41-60%'
        case slot >= 61 && slot <= 80:
          return '61-80%'
        case slot >= 81 && slot <= 100:
          return '81-100%'
        case slot >= 101 && slot <= 150:
          return '101-150%'
        case slot >= 151 && slot <= 200:
          return '151-200%'
        case slot >= 201:
          return '201%+'
        default:
          return 'unknown'
      }
    },

    getTimeLabel(location) {
      let slot = parseInt(location)

      slot += 1

      switch (true) {
        case slot >= 0 && slot <= 99:
          return '0-99ms'
        case slot >= 100 && slot <= 499:
          return '100-499ms'
        case slot >= 500 && slot <= 999:
          return '500-999ms'
        case slot >= 1000 && slot <= 1999:
          return '1-2s'
        case slot >= 2000 && slot <= 2999:
          return '2-3s'
        case slot >= 3000 && slot <= 3999:
          return '3-4s'
        case slot >= 4000:
          return '4s+'
        default:
          return 'unknown'
      }
    },

    share() {
      fireGtm('send', 'event', 'MFLY-Listings', 'Share menu', 'Share menu opened')
    },

    shareWait(ms) {
      fireGtm(
        'send',
        'event',
        'MFLY-Listings',
        'URL wait time',
        logicFunctions.fire.getTimeLabel(ms.millisTime - 1)
      )
    },

    addSetiDimension(
      gaDimensionId,
      testName,
      dimensionValue,
      gaEventName = 'seti',
      setiTeamName = 'mweb_flight'
    ) {
      const gaDimensionInfo = {
        [gaDimensionId]: `${testName}|${dimensionValue}`,
        event: gaEventName,
        seti_team_name: setiTeamName,
      }
      if (window.dataLayer) {
        window.dataLayer.push(gaDimensionInfo)
      }
    },

    sopqDealCheckbox(isChecked = false) {
      fireGtm('send', 'event', 'MFLY-Listings', 'APC Checkbox checked before submit', isChecked)
    },

    sopqAction(position, action) {
      fireGtm('send', 'gtm-custom-event', 'MFLY-Listings', `sopq_${action}`, position)
    },

    sopqUrl(url, label) {
      fireGtm('send', 'gtm-custom-event', 'MFLY-Listings', `${url}`, label)
    },

    sopqDealSelected(eventName, cheapSopq, choosenSopq, rcPreSelected) {
      const gaDimensionInfo = {
        event: eventName,
        originalFlight: cheapSopq,
        alternateFlight: choosenSopq,
        rentalCarIncluded: rcPreSelected,
      }

      if (window.dataLayer) {
        window.dataLayer.push(gaDimensionInfo)
      }
    },

    rcAction(position, action) {
      fireGtm('send', 'gtm-custom-event', 'MFLY-Listings', `rental_car_${action}`, position)
    },

    rentalCarCallError(error) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Rental Car Call Error', error)
    },

    splitLowestPriceFullResult: (itineraryTypeCode, isFused, isInterline) => {
      if (itineraryTypeCode !== 'OW') {
        fireGtm('send', 'event', 'MFLY-Listings', 'Lowest Price Search', splitGetLabel(isFused, isInterline))
      }
    },

    splitLowestPriceFilteredResult: (itineraryTypeCode, isFused, isInterline) => {
      if (itineraryTypeCode !== 'OW') {
        fireGtm(
          'send',
          'event',
          'MFLY-Listings',
          'Lowest Price Filtered Search',
          splitGetLabel(isFused, isInterline)
        )
      }
    },

    splitDetailsView: (isFused, isInterline) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Split Detail Views', splitGetLabel(isFused, isInterline))
    },

    cheapExpDeal: (isCheapestExpDeal) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'SOPQ_Cheapest', isCheapestExpDeal)
    },
    refIdMissing: (isMissing) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Ref id from deeplinking is missing', isMissing)
    },

    nearbyAirportChangeSearch: (label, noOfNearby) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Change Search', `${label}_Select_Nearby-${noOfNearby || 0}`)
    },

    nearbyAirportResult: () => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Nearby Airports Result', 'Incomplete Search Request')
    },

    popularFlights: (action) => {
      fireGtm('send', 'event', 'MFLY-Listings', action, 'Popular')
    },

    suggestedFlexDates: (isVariant, numOfDatesavailable) => {
      fireGtm(
        'send',
        'event',
        'MFLY-Listings',
        `Flex Date Component Clicked (${isVariant ? 'Variant' : 'Default'})`,
        `${numOfDatesavailable} dates available`
      )
    },

    detailsNullReload: (label) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Fly Details Null Reload', label)
    },

    listingsRenderTime: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Web Listings Render Time', value)
    },

    merchandising: (label) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Merchandising', label)
    },

    currentlyViewing: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Currently Viewing', value)
    },

    cugEligible: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Merchandising', value)
    },

    cugEligibleCount: (value) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Cug Eligible Count', value)
    },

    sponsoredCarrier: (label) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'Sponsored Carrier', label)
    },

    exdBagDisclosure: (label) => {
      fireGtm('send', 'event', 'MFLY-Listings', 'SOPQ Bag Disclosure', label)
    },

    hotelCrossSellPriceDiff: (priceDiff, isClick = false) => {
      let priceDiffEventLabel = 'price difference above 500'

      if (priceDiff <= 100) {
        priceDiffEventLabel = 'price difference 0-100'
      } else if (priceDiff <= 200) {
        priceDiffEventLabel = 'price difference 101-200'
      } else if (priceDiff <= 300) {
        priceDiffEventLabel = 'price difference 201-300'
      } else if (priceDiff <= 400) {
        priceDiffEventLabel = 'price difference 301-400'
      } else if (priceDiff <= 500) {
        priceDiffEventLabel = 'price difference 401-500'
      }

      fireGtm(
        'send',
        'event',
        'MFLY-Listings',
        `Package banner price${isClick ? ' click' : ''}`,
        priceDiffEventLabel
      )
    },

    priceConfirmation(message) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Price Confirm Result', message)
    },

    stellatePriceChange(label) {
      fireGtm('send', 'event', 'MFLY-Details', 'Stellate price change', label)
    },

    stellateHitOrMiss(label) {
      fireGtm('send', 'event', 'MFLY-Listings', 'Stellate air search', label)
    },

    toExpandCheckoutBarDetail() {
      fireGtm('send', 'event', 'MFLY-Details', 'expand_summaryofcharges')
    },

    toCheckout(message) {
      fireGtm('send', 'event', 'MFLY-Details', 'Redirect to Checkout', message)
    },

    seatMapsAvailable(wasSuccessFul = 'no', carrierCodeLabel = '') {
      fireGtm('send', 'event', 'MFLY-Details', wasSuccessFul, carrierCodeLabel)
    },

    fareBrandComparisonTextGATagging(airlineName) {
      fireGtm('send', 'event', 'MFLY-Details', 'Compare Upgrade Options', airlineName)
    },

    fireGenericListingsEvent: (action, label) => {
      fireGtm('send', 'event', 'MFLY-Listings', action, label)
    },

    upgradesAvailable: (label) => {
      fireGtm('send', 'event', 'MFLY-SEARCH', 'Upgrades Available', label)
    },

    filterRedirect(error, requestType) {
      fireGtm('send', 'event', 'MFLY-Listings', `${requestType} redirect error`, null, error)
    },

    dealsTutorialClicked: () => {
      fireGtm('send', 'event', 'Deals-Details', 'Deals Intro Copy', 'Tutorial Open')
    },

    dealsBaggageInfoOpened: () => {
      fireGtm('send', 'event', 'Deals-Details', 'click', 'Open SOPQ Baggage Info')
    },

    alternateExdClicked: (position, numOfAltDeals) => {
      fireGtm('send', 'event', 'Deals-Details', 'click', `Selected deal ${position} of ${numOfAltDeals}`)
    },

    expressDealSelected: (cheapestExd, selectedExd) => {
      let label
      let value
      if (selectedExd.priceKey) {
        label =
          selectedExd.priceKey === cheapestExd.priceKey ? 'Cheapest EXD selected' : 'Alternate EXD selected'
        value = `Cheapest deal priceKey: ${cheapestExd.priceKey}. Selected deal priceKey: ${selectedExd.priceKey}`
      } else {
        const cheapestExdItineraryReference = cheapestExd?.itineraryReference
          ? cheapestExd?.itineraryReference
          : cheapestExd?.itineraryReferenceInfo
        const selectedExdItineraryReference = selectedExd?.itineraryReference
          ? selectedExd?.itineraryReference
          : selectedExd?.itineraryReferenceInfo
        label =
          cheapestExdItineraryReference.refId === selectedExdItineraryReference.refId
            ? 'Cheapest EXD selected'
            : 'Alternate EXD selected'
        value = `Cheapest deal refId: ${cheapestExdItineraryReference}. Selected deal refId: ${selectedExdItineraryReference.refId}`
      }

      fireGtm('send', 'event', 'Deals-Details', 'click', label, value)
    },
  },
}

export { logicFunctions as flyGaLogic, fireGtm }
